/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2025.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */
import {Auth0Provider, useAuth0} from "@auth0/auth0-react";
import {QAuthenticationMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QAuthenticationMetaData";
import App, {SESSION_UUID_COOKIE_NAME} from "App";
import HandleAuthorizationError from "HandleAuthorizationError";
import {jwtDecode} from "jwt-decode";
import ProtectedRoute from "qqq/authorization/auth0/ProtectedRoute";
import {MaterialUIControllerProvider} from "qqq/context";
import AnalyticsUtils from "qqq/utils/analytics/AnalyticsUtils";
import {detectBasePath, getReturnToPath} from "qqq/utils/PathUtils";
import Client from "qqq/utils/qqq/Client";
import ProcessFilterStorage from "qqq/utils/qqq/ProcessFilterStorage";
import {useRef} from "react";
import {useCookies} from "react-cookie";
import {useNavigate, useSearchParams} from "react-router-dom";

const qController = Client.getInstance();
const qControllerV1 = Client.getInstanceV1();

interface Props
{
   setIsFullyAuthenticated?: (is: boolean) => void;
   setLoggedInUser?: (user: any) => void;
   setEarlyReturnForAuth?: (element: JSX.Element | null) => void;
}

/***************************************************************************
 ** hook for working with the Auth0 authentication module
 ***************************************************************************/
export default function useAuth0AuthenticationModule({setIsFullyAuthenticated, setLoggedInUser}: Props)
{
   const {user: auth0User, getAccessTokenSilently: auth0GetAccessTokenSilently, logout: useAuth0Logout} = useAuth0();

   const [cookies, removeCookie] = useCookies([SESSION_UUID_COOKIE_NAME]);

   ///////////////////////////////////////////////////////////////////////////////////////////////////////
   // localStorage key holding the path to restore after an involuntary (session-expiry / 401) re-login //
   ///////////////////////////////////////////////////////////////////////////////////////////////////////
   const postLoginRedirectPathKey = "auth0.postLoginRedirect.pathname";

   /////////////////////////////////////////////////////////////////////////////////
   // the stashed involuntary-logout path lives in localStorage, so an abandoned  //
   // re-login would otherwise leave it there indefinitely and hijack a later,    //
   // unrelated login that happens to land at root.  the logout -> re-login round //
   // trip takes seconds, so a short ttl bounds that stale window without         //
   // affecting the real flow.                                                    //
   /////////////////////////////////////////////////////////////////////////////////
   const postLoginRedirectTtlMillis = 5 * 60 * 1000;

   ////////////////////////////////////////////////////////////////////////////////////////
   // set once a voluntary log-out has started.  auth0's logout is a full-page redirect, //
   // so this flag lives only until the browser leaves the page - long enough to keep an //
   // in-flight request that 401s mid-redirect from re-stashing a path.                  //
   ////////////////////////////////////////////////////////////////////////////////////////
   const voluntaryLogoutInProgressRef = useRef(false);


   /***************************************************************************
    ** stash the current base-relative path (with a timestamp) so it can be
    ** restored after an involuntary logout + re-login round trip.
    ***************************************************************************/
   const stashPostLoginRedirectPath = (): void =>
   {
      localStorage.setItem(postLoginRedirectPathKey, JSON.stringify({path: getReturnToPath(), ts: new Date().getTime()}));
   };


   /***************************************************************************
    ** read the stashed involuntary-logout path, but only if it was stashed
    ** within postLoginRedirectTtlMillis.  returns null when absent, stale, or
    ** unparseable.  does not clear the stash - the caller does that.
    ***************************************************************************/
   const readStashedPostLoginRedirectPath = (): string | null =>
   {
      const raw = localStorage.getItem(postLoginRedirectPathKey);
      if (!raw)
      {
         return (null);
      }

      try
      {
         const parsed = JSON.parse(raw);
         if (parsed && parsed.path && typeof parsed.ts === "number" && (new Date().getTime() - parsed.ts) < postLoginRedirectTtlMillis)
         {
            return (parsed.path);
         }
      }
      catch (e)
      {
         console.log("Error parsing stashed post-login redirect path: " + e);
      }

      return (null);
   };


   /***************************************************************************
    ** compute the returnTo url handed to auth0's logout.
    ***************************************************************************/
   const buildLogoutReturnTo = (): string =>
   {
      ////////////////////////////////////////////////////////////////////////////////////////
      // where to send auth0's logout, so it lands back on a url the SPA is actually served //
      // from.  under a base path (e.g. /app) that must include the base path, else the     //
      // browser bounces to the bare origin - off the SPA - and the app never boots to      //
      // restore the stashed path.  at root this stays the bare origin (no trailing slash), //
      // exactly as before.                                                                 //
      // note: under a base path this returnTo (origin + basePath + "/") must be registered //
      // in the auth0 tenant's allowed logout urls.  at root nothing changes.               //
      ////////////////////////////////////////////////////////////////////////////////////////
      const basePath = detectBasePath();
      if (basePath && basePath !== "/")
      {
         return (`${window.location.origin}${basePath}/`);
      }
      return (window.location.origin);
   };


   /***************************************************************************
    **
    ***************************************************************************/
   const shouldStoreNewToken = (newToken: string, oldToken: string): boolean =>
   {
      if (!cookies[SESSION_UUID_COOKIE_NAME])
      {
         console.log("No session uuid cookie - so we should store a new one.");
         return (true);
      }

      if (!oldToken)
      {
         console.log("No accessToken in localStorage - so we should store a new one.");
         return (true);
      }

      try
      {
         const oldJSON: any = jwtDecode(oldToken);
         const newJSON: any = jwtDecode(newToken);

         ////////////////////////////////////////////////////////////////////////////////////
         // if the old (local storage) token is expired, then we need to store the new one //
         ////////////////////////////////////////////////////////////////////////////////////
         const oldExp = oldJSON["exp"];
         if (oldExp * 1000 < (new Date().getTime()))
         {
            console.log("Access token in local storage was expired - so we should store a new one.");
            return (true);
         }

         ////////////////////////////////////////////////////////////////////////////////////////////////
         // remove the exp & iat values from what we compare - as they are always different from auth0 //
         // note, this is only deleting them from what we compare, not from what we'd store.           //
         ////////////////////////////////////////////////////////////////////////////////////////////////
         delete newJSON["exp"];
         delete newJSON["iat"];
         delete oldJSON["exp"];
         delete oldJSON["iat"];

         const different = JSON.stringify(newJSON) !== JSON.stringify(oldJSON);
         if (different)
         {
            console.log("Latest access token from auth0 has changed vs localStorage - so we should store a new one.");
         }
         return (different);
      }
      catch (e)
      {
         console.log("Caught in shouldStoreNewToken: " + e);
      }

      return (true);
   };


   /***************************************************************************
    **
    ***************************************************************************/
   const readStoredSessionValues = (): {[key: string]: any} | null =>
   {
      try
      {
         const rawSessionValues = localStorage.getItem("sessionValues");
         return (rawSessionValues ? JSON.parse(rawSessionValues) : null);
      }
      catch (e)
      {
         console.log("Error parsing sessionValues from localStorage: " + e);
         return (null);
      }
   };


   /***************************************************************************
    **
    ***************************************************************************/
   const hasAnalyticsIdentityValues = (sessionValues: {[key: string]: any} | null): boolean =>
   {
      const analyticsIdentityValues = AnalyticsUtils.getAnalyticsIdentityValues(sessionValues);
      return Boolean(
         analyticsIdentityValues["user_email"]
         || sessionValues?.user?.email
         || sessionValues?.user?.idReference
         || sessionValues?.user?.id
         || sessionValues?.user?.userId
      );
   };


   /***************************************************************************
    **
    ***************************************************************************/
   const setupSession = async () =>
   {
      try
      {
         console.log("Loading token from auth0...");
         const accessToken = await auth0GetAccessTokenSilently();
         let loggedInUserForApp = auth0User;

         const lsAccessToken = localStorage.getItem("accessToken");
         if (shouldStoreNewToken(accessToken, lsAccessToken))
         {
            console.log("Sending accessToken to backend, requesting a sessionUUID...");
            const {values} = await qController.manageSession(accessToken, null);

            localStorage.setItem("accessToken", accessToken);
            localStorage.setItem("sessionValues", JSON.stringify(values ?? {}));
            loggedInUserForApp = values?.user ?? auth0User;
            console.log("Got new sessionUUID from backend, and stored new accessToken");
         }
         else
         {
            const storedSessionValues = readStoredSessionValues();
            if (!hasAnalyticsIdentityValues(storedSessionValues))
            {
               console.log("Refreshing session values from existing sessionUUID...");
               const {values} = await qController.manageSession(null, cookies[SESSION_UUID_COOKIE_NAME]);
               localStorage.setItem("sessionValues", JSON.stringify(values ?? {}));
               loggedInUserForApp = values?.user ?? auth0User;
            }
            else if (storedSessionValues?.user)
            {
               loggedInUserForApp = storedSessionValues.user;
            }

            console.log("Using existing sessionUUID cookie");
         }

         setIsFullyAuthenticated(true);
         Client.setGotAuthenticationInAllControllers();

         setLoggedInUser(loggedInUserForApp);
         console.log("Token load complete.");
      }
      catch (e)
      {
         console.log(`Error loading token: ${JSON.stringify(e)}`);
         qController.clearAuthenticationMetaDataLocalStorage();
         localStorage.removeItem("accessToken");
         localStorage.removeItem("sessionValues");
         removeCookie(SESSION_UUID_COOKIE_NAME, {path: "/"});
         ///////////////////////////////////////////////////////////////////////////////////
         // token load failed - treat this as an involuntary logout and stash the current //
         // path, so the user is returned here after logging back in.                     //
         ///////////////////////////////////////////////////////////////////////////////////
         stashPostLoginRedirectPath();
         await ProcessFilterStorage.deleteAllFilters();
         useAuth0Logout({logoutParams: {returnTo: buildLogoutReturnTo()}});
         return;
      }
   };


   /***************************************************************************
    **
    ***************************************************************************/
   const logout = (options?: {preserveReturnPath?: boolean}) =>
   {
      ///////////////////////////////////////////////////////////////////////////////////////
      // on an involuntary logout (session expiry / 401), stash the current base-relative  //
      // path so it can be restored after the user logs back in.  a voluntary logout       //
      // (options omitted) intentionally does not, so a deliberate log-out lands on home - //
      // and it latches, since auth0's redirect is async, and a request that 401s in that  //
      // window would otherwise re-stash the path the log-out just cleared.                //
      ///////////////////////////////////////////////////////////////////////////////////////
      if (options?.preserveReturnPath && !voluntaryLogoutInProgressRef.current)
      {
         stashPostLoginRedirectPath();
      }
      else
      {
         /////////////////////////////////////////////////////////////////////////////////////
         // a deliberate log-out should land on home - clear any path stashed by an earlier //
         // involuntary logout that a silent re-auth never consumed, and latch, so neither  //
         // that stale path nor a late 401 can leak into this login.                        //
         /////////////////////////////////////////////////////////////////////////////////////
         voluntaryLogoutInProgressRef.current = true;
         localStorage.removeItem(postLoginRedirectPathKey);
      }
      localStorage.removeItem("accessToken");
      localStorage.removeItem("sessionValues");
      useAuth0Logout({logoutParams: {returnTo: buildLogoutReturnTo()}});
   };


   /***************************************************************************
    **
    ***************************************************************************/
   // @ts-ignore
   function Auth0ProviderWithRedirectCallback({children, ...props})
   {
      const navigate = useNavigate();
      const [searchParams] = useSearchParams();

      // @ts-ignore
      const onRedirectCallback = (appState) =>
      {
         /////////////////////////////////////////////////////////////////////////////////////////
         // prefer this login's appState.returnTo - a real deep link wins.  if it is absent or  //
         // just root (e.g. after an involuntary logout bounced us to origin), restore the path //
         // stashed before that logout.  always clear the stash.  the final fallback uses       //
         // pathname only, since window.location.search still holds auth0's code/state here.    //
         // the root test is a sentinel, not proof of origin: a login that legitimately lands   //
         // at root - a user typing the bare url - also consumes an unconsumed stash, and with  //
         // several tabs open the stash is last-writer-wins.  both are same-user/same-browser   //
         // surprises rather than harm, and the stash ttl is what bounds them.                  //
         /////////////////////////////////////////////////////////////////////////////////////////
         let target = appState && appState.returnTo;
         if (!target || target === "/")
         {
            const stashedPath = readStashedPostLoginRedirectPath();
            if (stashedPath)
            {
               target = stashedPath;
            }
         }
         localStorage.removeItem(postLoginRedirectPathKey);
         navigate(target || getReturnToPath(false));
      };
      if (searchParams.get("error"))
      {
         return (
            // @ts-ignore
            <Auth0Provider {...props}>
               <HandleAuthorizationError errorMessage={searchParams.get("error_description")} />
            </Auth0Provider>
         );
      }
      else
      {
         return (
            // @ts-ignore
            <Auth0Provider onRedirectCallback={onRedirectCallback} {...props}>
               {children}
            </Auth0Provider>
         );
      }
   }


   /***************************************************************************
    **
    ***************************************************************************/
   const renderAppWrapper = (authenticationMetaData: QAuthenticationMetaData): JSX.Element =>
   {
      // @ts-ignore
      let domain: string = authenticationMetaData.data.baseUrl;

      // @ts-ignore
      const clientId = authenticationMetaData.data.clientId;

      // @ts-ignore
      const audience = authenticationMetaData.data.audience;

      if (!domain || !clientId)
      {
         return (
            <div>Error: AUTH0 authenticationMetaData is missing baseUrl [{domain}] and/or clientId [{clientId}].</div>
         );
      }

      if (domain.endsWith("/"))
      {
         /////////////////////////////////////////////////////////////////////////////////////
         // auth0 lib fails if we have a trailing slash.  be a bit more graceful than that. //
         /////////////////////////////////////////////////////////////////////////////////////
         domain = domain.replace(/\/$/, "");
      }

      /***************************************************************************
       ** simple Functional Component to wrap the <App> and pass the authentication-
       ** MetaData prop in, so a simple Component can be passed into ProtectedRoute
       ***************************************************************************/
      function WrappedApp()
      {
         return <App authenticationMetaData={authenticationMetaData} />
      }

      /////////////////////////////////////////////////////////////////////////////////////
      // include the base path in the auth0 redirect uri, so that when the app is served //
      // under a base path (e.g. /app), auth0 returns to that base path rather than the  //
      // domain root.  at root (basePath "/") this is just origin + "/", as before.      //
      // note: this redirect uri must be registered in the auth0 tenant's allowed        //
      // callback urls.                                                                  //
      /////////////////////////////////////////////////////////////////////////////////////
      const basePath = detectBasePath();
      const redirectUri = `${window.location.origin}${basePath && basePath !== "/" ? basePath : ""}/`;

      return (
         <Auth0ProviderWithRedirectCallback
            domain={domain}
            clientId={clientId}
            authorizationParams={{audience: audience, redirect_uri: redirectUri}}>
            <MaterialUIControllerProvider>
               <ProtectedRoute component={WrappedApp} />
            </MaterialUIControllerProvider>
         </Auth0ProviderWithRedirectCallback>
      );
   };


   return {
      setupSession,
      logout,
      renderAppWrapper
   };

}
